const livePriceClockSubscribers = new Set();
let livePriceClockInterval = null;

export const subscribeToLivePriceClock = (subscriber) => {
  livePriceClockSubscribers.add(subscriber);
  if (livePriceClockInterval === null) {
    livePriceClockInterval = setInterval(() => {
      const now = Date.now();
      livePriceClockSubscribers.forEach((update) => update(now));
    }, 1000);
  }

  return () => {
    livePriceClockSubscribers.delete(subscriber);
    if (livePriceClockSubscribers.size === 0 && livePriceClockInterval !== null) {
      clearInterval(livePriceClockInterval);
      livePriceClockInterval = null;
    }
  };
};

export const getLivePriceChangePercent = (quote) => {
  const value = quote?.change_percent;
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace('%', '').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const formatLivePriceAge = (timestamp, now) => {
  const totalSeconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s ago`;
  if (totalSeconds < 3600) return `${Math.floor(totalSeconds / 60)}m ago`;

  const seconds = String(totalSeconds % 60).padStart(2, '0');
  const minutes = String(Math.floor(totalSeconds / 60) % 60).padStart(2, '0');
  const totalHours = Math.floor(totalSeconds / 3600);
  const hours = String(totalHours % 24).padStart(2, '0');

  if (totalSeconds < 86400) {
    return `${totalHours}:${minutes}:${seconds} ago`;
  }

  const days = Math.floor(totalSeconds / 86400);
  return `${days} day${days === 1 ? '' : 's'} ${hours}:${minutes}:${seconds} ago`;
};

export const formatScannedAge = (timestamp, now) => {
  if (timestamp === null || timestamp === undefined || timestamp === '') return 'Age unavailable';
  const epoch = new Date(timestamp).getTime();
  if (!Number.isFinite(epoch) || !Number.isFinite(now)) return 'Age unavailable';
  const seconds = Math.max(0, Math.floor((now - epoch) / 1000));
  const parts = [
    Math.floor(seconds / 86400),
    Math.floor(seconds / 3600) % 24,
    Math.floor(seconds / 60) % 60,
    seconds % 60,
  ];
  return `${parts.map(part => String(part).padStart(2, '0')).join(':')} ago`;
};

const statusTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

export const formatStatusAge = (timestamp, now) => {
  if (typeof timestamp !== 'string' || !timestamp.trim()) return 'Age unavailable';
  const value = timestamp.trim();
  if (/(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) return formatScannedAge(value, now);

  // Azure SQL stores status_datetime as a timezone-naive New York wall time.
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/.exec(value);
  if (!match) return 'Age unavailable';
  const [, year, month, day, hour, minute, second] = match;
  const wallTime = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  const candidates = ['-04:00', '-05:00'].map(offset => new Date(`${wallTime}${offset}`))
    .filter(date => {
      if (!Number.isFinite(date.getTime())) return false;
      const parts = Object.fromEntries(statusTimeFormatter.formatToParts(date).map(part => [part.type, part.value]));
      return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}` === wallTime;
    });
  // A repeated DST hour has no reliable offset in legacy data.
  return candidates.length === 1 ? formatScannedAge(candidates[0].toISOString(), now) : 'Age unavailable';
};
